import { Location, RunAction, RunDriver, RunEquipment as DispatchRunEquipment, RunPlanActivity as DispatchActivity } from "./dispatch.model";
import { Driver } from "./driver.model";
import { Fleet as NonstandardFleetStructure, FleetSubtype } from "./fleet.model";
import { FleetType } from "./fleetserviceinterval.model";
import { User } from "./users.model";

export type Pivot<to, from> = to & { pivot?: from };

export type Fleet = NonstandardFleetStructure & {
  type   ?: FleetType;
  subtype?: FleetSubtype;
}

export type RunEquipment = DispatchRunEquipment & {
  fleet: Fleet;
}

export type RunEquipmentInput = Pick<
  RunEquipment,
  'fleet' | 'id' | 'run_id' | 'equipment_id'
>;

export type RunPlanActivity = DispatchActivity & {
  run     ?: Run;
  location?: Location;
  actual  ?: RunEvent;
  creator ?: User;
  geofence?: Geofence;
}

/**
 * Canonical run model.
 * 
 * The one from _dispatch.model.ts_ has all sorts of custom
 * properties attached to it for the purpose of the dispatch
 * planner, which is not really usable in other components
 * other than the planner itself.
 * 
 * This run model is based from its structure from the database,
 * and additional optional properties are from the runs' related
 * models as defined in the server's `App\Models\Run` class.
 */
export interface Run {
  id                                 : number;
  uuid                               : string | null;
  control_no                         : string;
  run_type_id                        : RunType;
  linehaul_type                      : 'ftl' | 'ltl' | '' | null;
  run_status_id                      : number;
  sdp_file                           : number | null;
  tripsheet_file                     : string | null;
  changeover_run_id                  : number;
  run_routes_id                      : number | null;
  total_klms                         : number | null;
  total_driving_hours                : number;
  departure_date                     : string;
  departure_time                     : string | null;
  arrival_date                       : string;
  arrival_time                       : string | null;
  is_released                        : number;
  service_type_id                    : number | null;
  admin_notes                        : string;
  driver_notes                       : string;
  dispatch_notes                     : string | null;
  is_excluded_saturday               : number;
  is_excluded_sunday                 : number | null;
  is_excluded_holiday_no             : number | null;
  excluded_holiday_dates             : string;
  is_driver_fitness_for_duty_checked : number;
  is_fleet_checked                   : number | null;
  is_work_diary_submitted            : number | null;
  is_fatigue_requirements_checked    : number;
  is_location_checked                : number | null;
  is_load_restraint_checked          : number | null;
  is_mass_weights_checked            : number | null;
  is_non_compliant                   : number | null;
  total_work_hours                   : number;
  other_work_hours                   : number | null;
  min_long_rest_hours                : number | null;
  min_short_rest_hours               : number | null;
  other_rest_hours                   : number | null;
  created_by                         : number;
  updated_by                         : number;
  created_at                         : string;
  updated_at                         : string;

  drivers                            : Pivot<Driver, RunDriver>[];
  truck                             ?: RunEquipment | RunEquipmentInput;
  trailers                          ?: Fleet[];
  activities                        ?: RunPlanActivity[];
  actual                            ?: RunEvent[];
}

/**
 * Cannonical structure of the run event based on its
 * definition in the database.
 */
export interface RunEvent {
  id                   : number;
  run_id               : number;
  run_action_id        : number;
  run_action          ?: RunAction;
  location            ?: Location | null;
  run_plan_activity_id : number | null;
  run_plan_activity   ?: RunPlanActivity;
  start                : string | null;
  end                  : string | null;
  start_utc           ?: string | null;
  end_utc             ?: string | null;
  created_by           : number;
  created_at           : string | null;
  updated_at           : string;
}

export interface Geofence {
  id                  : number;
  run_plan_activity_id: number;
  radius              : number;
  created_by          : number;
  created_at          : string;
}

export enum RunActions {
  Pickup = 1,
  Deliver,
  Release,
  Collect,
  Start,
  End,
  Breakdown,
  Refuel,
  TrailerRelease,
  TrailerCollect,
  Create,
  Accept,
  Reject,
  EmptyRunStart,
  EmptyRunEnd,
  UploadPaperwork
}

export enum TaskStatuses {
  Done,
  NotDone
}

export enum RunStatus {
  Pending = 1,
  Dispatched,
  Processing,
  Completed,
  Cancelled,
  Withdrawn,
  Subcontracted,
  Draft
}

export enum RunType {
  LOCAL = 1,
  LINEHAUL,
  EMPTY_RUN,
  TOW_HAUL,
  TRAILER_HIRE
}
 
export enum ServiceType {
  Car = 1,
  TwoTonneFourPallets = 2,
  SemiPrimeMoverOnly = 3,
  SemiPrimeMoverOnlyWithTrailer = 4,
  BdblRatedPrimeMoverOnly = 5,
  BdblRatedPrimeMoverWithTrailers = 6,
  ATrailer = 7,
  BdblOnly = 5,
  SixTonneEightPallets = 8,
  TwelveTonneFourteenPallets = 9,
  RoadTrain = 10,
  BTrailer = 11,
  BdblRatedPrimeMoverWithATrailer = 12,
  BdblRatedPrimeMoverWithBTrailer = 13
}
